import { put, call, takeEvery } from "redux-saga/effects";
import * as apis from "../../network/apis/auth";
import * as companyPortalApis from "../../network/apis/companyPortalAuth";
import * as clientPortalApis from "../../network/apis/clientPortalAuth";
import * as types from "./types";
import * as actions from "./actions";
import {
  clearAdminData,
  clearOppositePortalStorage,
  getSessionPortal,
  setAdmin,
  setDeviceId,
} from "../../utils/Auth";
import {
  AUTH_CONTEXT_TYPE,
  isClientPublicAuthPath,
  isCompanyPublicAuthPath,
  resolvePortalLoginPath,
} from "../../utils/authPortal";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import {
  getCompanyLoginPostAuthRoute,
  isCompanyDeactivated,
  isCompanyRegistrationIncompleteState,
  normalizeCompanyPortalSessionUser,
  PENDING_COMPANY_RESET_PASSWORD_FLOW_KEY,
} from "../../utils/companyRegistration";
import {
  applyClientCompanySelection,
  getClientCompanies,
  getClientSelectedCompany,
  PENDING_CLIENT_RESET_PASSWORD_FLOW_KEY,
  resolveClientPostLoginRoute,
} from "../../utils/clientPortal";
import { showErrorMsg, showSuccessMsg } from "../../utils/Helpers";
import {
  clearFirebaseToken,
  generateFirebaseToken,
} from "../../firebase/helpers";
import store from "../index";
import { setUnseenNotificationsCount } from "../Notifications/actions";
import {
  isAdmin,
  isLoginUser,
  isSuperAdmin,
  isUser,
  isVisitor,
  rolePermissions,
  getCompanyPortalHomeRoute,
} from "../../utils/Permissions";
import { otherRolesStatus } from "../../utils/Constants";
import history from "../../routes/History";

const getAdminPortalUserTypeFromRole = (roleName) => {
  switch (roleName) {
    case "super admin":
      return "admin";
    case "client":
      return "client";
    case "supervisor":
      return "supervisor";
    default:
      return "visitor";
  }
};

const resolveLoginApi = ({ userType, isCompanyPortalLogin, isClientPortalLogin }) => {
  const isPortalUser = ["admin", "visitor", "client"].includes(userType);

  if (userType === "supervisor") return apis.userAppLogin;
  if (!isPortalUser) return apis.userAppLogin;
  if (isCompanyPortalLogin) return companyPortalApis.companyLogin;
  if (isClientPortalLogin) return clientPortalApis.clientLogin;
  return apis.login;
};

const resolveActivePortal = ({
  elevateCompanyLoginToAdminPortal,
  isCompanyPortalLogin,
  isClientPortalLogin,
}) => {
  if (elevateCompanyLoginToAdminPortal) return AUTH_CONTEXT_TYPE.ADMIN;
  if (isCompanyPortalLogin) return AUTH_CONTEXT_TYPE.COMPANY;
  if (isClientPortalLogin) return AUTH_CONTEXT_TYPE.CLIENT;
  return AUTH_CONTEXT_TYPE.ADMIN;
};

const resolveLogoutApi = ({ sessionPortal, userType }) => {
  switch (sessionPortal) {
    case AUTH_CONTEXT_TYPE.COMPANY:
      return companyPortalApis.companyLogout;
    case AUTH_CONTEXT_TYPE.CLIENT:
      return clientPortalApis.clientLogout;
    case AUTH_CONTEXT_TYPE.ADMIN:
    default:
      return userType === "supervisor"
        ? apis.userAppLogout
        : userType === "admin" || userType === "visitor"
        ? apis.logout
        : apis.userAppLogout;
  }
};

const resolveLoginRouteAfterLogout = (sessionPortal, pathname) =>
  resolvePortalLoginPath({ sessionPortal, pathname });

const resolveForgetPasswordApi = ({ fromCompanyFlow, fromClientFlow }) => {
  if (fromCompanyFlow) return companyPortalApis.companyForgetPassword;
  if (fromClientFlow) return clientPortalApis.clientForgetPassword;
  return apis.forgetPassword;
};

const resolveResetPasswordSuccessRoute = ({
  fromCompanyFlow,
  fromClientFlow,
}) => {
  if (fromCompanyFlow) return ROUTE_PATHS.companyResetPasswordSuccess;
  if (fromClientFlow) return ROUTE_PATHS.clientResetPasswordSuccess;
  return ROUTE_PATHS.resetPasswordSuccess;
};

export function* loginSaga({ payload }) {
  try {
    const isCompanyPortalLogin =
      payload.authPortal === AUTH_CONTEXT_TYPE.COMPANY;
    const isClientPortalLogin =
      payload.authPortal === AUTH_CONTEXT_TYPE.CLIENT;

    const loginApi = resolveLoginApi({
      userType: payload.userType,
      isCompanyPortalLogin,
      isClientPortalLogin,
    });
    const response = yield call(loginApi, payload.data);

    if (!response?.data?.data) {
      throw new Error("Invalid login response");
    }

    const device_id = payload.data.device_id;
    const { data } = response.data;
    const roleName = data?.role?.name_en?.toLowerCase();

    // The credentials are accepted, but a deactivated company's portal doesn't open: nothing
    // is stored, the user only gets the blocked screen (PROLINE-4124).
    if (
      isCompanyPortalLogin &&
      isCompanyDeactivated(normalizeCompanyPortalSessionUser(data))
    ) {
      yield history.push(ROUTE_PATHS.companyDeactivated);
      return;
    }

    const elevateCompanyLoginToAdminPortal =
      isCompanyPortalLogin && roleName === "super admin";

    const resolvedUserType =
      isCompanyPortalLogin || isClientPortalLogin
        ? payload.userType
        : payload.userType === "supervisor"
        ? "supervisor"
        : getAdminPortalUserTypeFromRole(roleName);

    const activePortal = resolveActivePortal({
      elevateCompanyLoginToAdminPortal,
      isCompanyPortalLogin,
      isClientPortalLogin,
    });
    yield clearOppositePortalStorage(activePortal);
    yield localStorage.setItem(
      "userType",
      elevateCompanyLoginToAdminPortal ? "admin" : resolvedUserType,
    );
    yield localStorage.setItem(
      "sessionPortal",
      activePortal,
    );

    const loginPayload = normalizeCompanyPortalSessionUser(
      isCompanyPortalLogin && data.must_change_password
        ? { ...data, [PENDING_COMPANY_RESET_PASSWORD_FLOW_KEY]: true }
        : isClientPortalLogin && data.must_change_password
        ? { ...data, [PENDING_CLIENT_RESET_PASSWORD_FLOW_KEY]: true }
        : data,
    );

    yield put(actions.loginResponse(loginPayload));
    const unseenCount = data.admin_portal_unseen_notifications_count ?? 0;
    yield put(setUnseenNotificationsCount(unseenCount));
    yield localStorage.setItem("unseenNotificationsCount", unseenCount);

    yield setAdmin(loginPayload);
    yield setDeviceId(device_id);

    if (isCompanyPortalLogin && data.must_change_password) {
      yield history.push(ROUTE_PATHS.companyResetPassword, {
        email: data.email,
        advanceToStep2: true,
        resetPasswordFormKey: Date.now(),
      });
      return;
    }

    if (
      isCompanyPortalLogin &&
      !elevateCompanyLoginToAdminPortal &&
      isCompanyRegistrationIncompleteState(data)
    ) {
      yield history.push(getCompanyLoginPostAuthRoute(data));
      return;
    }

    if (resolvedUserType === "supervisor") {
      yield history.push(
        store.getState().auth.login.must_change_password
          ? ROUTE_PATHS.changePassword
          : ROUTE_PATHS.events
      );
      return;
    }

    if (isCompanyPortalLogin && !elevateCompanyLoginToAdminPortal) {
      yield history.push(getCompanyPortalHomeRoute(loginPayload));
      return;
    }

    if (isClientPortalLogin && data.must_change_password) {
      yield history.push(ROUTE_PATHS.clientResetPassword, {
        email: data.email,
        advanceToStep2: true,
        resetPasswordFormKey: Date.now(),
      });
      return;
    }

    if (isClientPortalLogin) {
      const companies = getClientCompanies(data);
      let clientLoginPayload = data;
      if (companies.length === 1) {
        clientLoginPayload = applyClientCompanySelection(data, companies[0]);
        yield put(actions.loginResponse(clientLoginPayload));
        yield setAdmin(clientLoginPayload);
      }
      yield history.push(resolveClientPostLoginRoute(clientLoginPayload));
      return;
    }

    const { status } = rolePermissions() || {};
    const isVisitorLogin = isVisitor();

    /** Fresh source of truth for the redirect — never the stale
     * `login.role.permissions` snapshot (see PERMISSIONS_DIAGNOSIS.md). */
    let permissions = [];
    if (isVisitorLogin) {
      const permsRes = yield call(apis.getPermissions, { id: data.role?.id });
      permissions = permsRes.data?.data ?? [];
      yield put(actions.getPermissionResponse(permissions));
    }

    const allPermission = permissions.some(
      (item) =>
        Boolean(item.can_view) ||
        Boolean(item.can_add) ||
        Boolean(item.can_edit) ||
        Boolean(item.can_delete)
    );

    const activeRoute = permissions.find((item) => Boolean(item.can_view));

    yield history.push(
      data.must_change_password
        ? ROUTE_PATHS.changePassword
        : isAdmin() && !isLoginUser() && isSuperAdmin()
        ? ROUTE_PATHS.adminDashboard
        : (isAdmin() && !isLoginUser()) || isUser()
        ? ROUTE_PATHS.events
        : isVisitorLogin && status === otherRolesStatus.enabled && allPermission
        ? activeRoute?.permission_url
        : ROUTE_PATHS.noPermission
    );
  } catch (error) {
    yield put(actions.loginResponse({}));
    yield clearAdminData();
    let errorMsg =
      error.response?.data?.message ?? error.message ?? "";
    if (Array.isArray(errorMsg)) {
      errorMsg = errorMsg.filter(Boolean).join(", ");
    }
    if (typeof errorMsg !== "string") {
      errorMsg = String(errorMsg);
    }
    const normalized = errorMsg.trim();
    if (normalized) {
      yield call(showErrorMsg, normalized);
    }
    const redirectTo =
      payload.failureRedirectPath || ROUTE_PATHS.login;
    if (payload.failureRedirectState != null) {
      yield history.push(redirectTo, {
        ...payload.failureRedirectState,
      });
    } else {
      yield history.push(redirectTo);
    }
  }
}

function* logoutSaga({ payload }) {
  try {
    const userType = localStorage.getItem("userType");
    const sessionPortal = getSessionPortal();

    const logoutApi = resolveLogoutApi({ sessionPortal, userType });

    yield call(logoutApi, payload);
    yield put(actions.loginResponse({}));
    const loginRouteAfterLogout = resolveLoginRouteAfterLogout(
      sessionPortal,
      history.location?.pathname,
    );
    yield clearAdminData();
    yield clearFirebaseToken();
    yield generateFirebaseToken();

    yield history.push(loginRouteAfterLogout);
  } catch (error) {
    console.log("Logout error", error);
  }
}

function normalizeChangePasswordErrorMessage(error) {
  let errorMsg = error?.response?.data?.message ?? error?.message ?? "";
  if (Array.isArray(errorMsg)) {
    errorMsg = errorMsg.filter(Boolean).join(", ");
  }
  if (typeof errorMsg !== "string") {
    errorMsg = String(errorMsg);
  }
  return errorMsg.trim();
}

/** Admin portal only — POST /admins/change_password */
function* changePasswordSaga({ payload }) {
  try {
    const response = yield call(apis.changePassword, payload.data);

    if (payload.from === "change") {
      const adminData = store.getState().auth.login;
      const mustChange =
        response?.data?.data?.must_change_password ??
        adminData.must_change_password;

      yield put(
        actions.loginResponse({
          ...adminData,
          must_change_password: mustChange,
        }),
      );

      yield setAdmin({
        ...adminData,
        must_change_password: mustChange,
      });

      const userType = localStorage.getItem("userType");
      const { permissions, status } = rolePermissions() || {};
      const allPermission = permissions?.some(
        (item) =>
          item.can_view ||
          item.can_add ||
          item.can_edit ||
          item.can_delete === true,
      );
      const activeRoute = permissions?.find(
        (item) => item.can_view === true,
      );
      const redirectTo =
        userType === "admin" && isSuperAdmin()
          ? ROUTE_PATHS.adminDashboard
          : userType === "admin" || userType === "client"
          ? ROUTE_PATHS.events
          : userType === "visitor" &&
            status === otherRolesStatus.enabled &&
            allPermission
          ? activeRoute?.permission_url
          : ROUTE_PATHS.noPermission;

      yield history.push(redirectTo);
    } else {
      yield history.push(ROUTE_PATHS.profile);
    }
    yield call(showSuccessMsg, "changePassword");
  } catch (error) {
    const normalized = normalizeChangePasswordErrorMessage(error);
    if (normalized) {
      yield call(showErrorMsg, normalized);
    }
  } finally {
    yield put(actions.changePasswordSettled());
  }
}

/** Company portal — POST /company_users/change_password */
function* changeCompanyPasswordSaga({ payload }) {
  try {
    const response = yield call(
      companyPortalApis.companyChangePassword,
      payload.data,
    );

    if (payload.from === "change") {
      const adminData = store.getState().auth.login;
      const patch = response?.data?.data ?? {};
      const merged = { ...adminData, ...patch };
      const nextMustChange =
        patch.must_change_password ?? merged.must_change_password;

      const nextLogin = {
        ...merged,
        must_change_password: nextMustChange,
      };
      if (!nextMustChange) {
        delete nextLogin[PENDING_COMPANY_RESET_PASSWORD_FLOW_KEY];
      }

      yield put(actions.loginResponse(nextLogin));
      yield setAdmin(nextLogin);

      if (
        localStorage.getItem("sessionPortal") === "company" &&
        isCompanyRegistrationIncompleteState(merged)
      ) {
        yield history.push(getCompanyLoginPostAuthRoute(merged));
        yield call(showSuccessMsg, "changePassword");
        return;
      }

      if (localStorage.getItem("sessionPortal") === "company") {
        yield history.push(getCompanyPortalHomeRoute(nextLogin));
        yield call(showSuccessMsg, "changePassword");
        return;
      }

      const userType = localStorage.getItem("userType");
      const { permissions, status } = rolePermissions() || {};
      const allPermission = permissions?.some(
        (item) =>
          item.can_view ||
          item.can_add ||
          item.can_edit ||
          item.can_delete === true,
      );
      const activeRoute = permissions?.find(
        (item) => item.can_view === true,
      );
      const redirectTo =
        userType === "admin" || userType === "client"
          ? ROUTE_PATHS.companyHome
          : userType === "visitor" &&
            status === otherRolesStatus.enabled &&
            allPermission
          ? activeRoute?.permission_url
          : ROUTE_PATHS.noPermission;
      yield history.push(redirectTo);
    } else {
      yield history.push(ROUTE_PATHS.profile);
    }
    yield call(showSuccessMsg, "changePassword");
  } catch (error) {
    const normalized = normalizeChangePasswordErrorMessage(error);
    if (normalized) {
      yield call(showErrorMsg, normalized);
    }
  } finally {
    yield put(actions.changePasswordSettled());
  }
}

/** Client portal — POST /clients/change_password */
function* changeClientPasswordSaga({ payload }) {
  try {
    const response = yield call(
      clientPortalApis.clientChangePassword,
      payload.data,
    );

    if (payload.from === "change") {
      const adminData = store.getState().auth.login;
      const patch = response?.data?.data ?? {};
      const merged = { ...adminData, ...patch };
      const nextMustChange =
        patch.must_change_password ?? merged.must_change_password;

      let nextLogin = {
        ...merged,
        must_change_password: nextMustChange,
      };
      if (!nextMustChange) {
        delete nextLogin[PENDING_CLIENT_RESET_PASSWORD_FLOW_KEY];
      }

      const companies = getClientCompanies(nextLogin);
      if (
        !nextMustChange &&
        companies.length === 1 &&
        !getClientSelectedCompany(nextLogin)
      ) {
        nextLogin = applyClientCompanySelection(nextLogin, companies[0]);
      }

      yield put(actions.loginResponse(nextLogin));
      yield setAdmin(nextLogin);
      yield history.push(resolveClientPostLoginRoute(nextLogin));
    } else {
      yield history.push(ROUTE_PATHS.clientPortalDashboard);
    }
    yield call(showSuccessMsg, "changePassword");
  } catch (error) {
    const normalized = normalizeChangePasswordErrorMessage(error);
    if (normalized) {
      yield call(showErrorMsg, normalized);
    }
  } finally {
    yield put(actions.changePasswordSettled());
  }
}

function* forgetPasswordSaga({ payload }) {
  try {
    const fromCompanyFlow = isCompanyPublicAuthPath(history.location.pathname);
    const fromClientFlow = isClientPublicAuthPath(history.location.pathname);
    const forgetFn = resolveForgetPasswordApi({
      fromCompanyFlow,
      fromClientFlow,
    });
    yield call(forgetFn, payload);
    yield history.push(
      resolveResetPasswordSuccessRoute({ fromCompanyFlow, fromClientFlow }),
      { email: payload?.email },
    );
  } catch (error) {
    const normalized = normalizeChangePasswordErrorMessage(error);
    if (normalized) {
      yield call(showErrorMsg, normalized);
    }
  }
}

function* getPermissionSaga({ payload }) {
  if (getSessionPortal() === AUTH_CONTEXT_TYPE.COMPANY) {
    return;
  }
  if (getSessionPortal() === AUTH_CONTEXT_TYPE.CLIENT) {
    return;
  }
  try {
    const response = yield call(apis.getPermissions, payload);
    yield put(actions.getPermissionResponse(response.data?.data));
  } catch (error) {
    yield error.response && showErrorMsg(error.response?.data?.message);
  }
}

export default function* AuthSagas() {
  yield takeEvery(types.LOGIN_REQUEST, loginSaga);
  yield takeEvery(types.LOGOUT_REQUEST, logoutSaga);
  yield takeEvery(types.CHANGE_PASSWORD_REQUEST, changePasswordSaga);
  yield takeEvery(
    types.CHANGE_COMPANY_PASSWORD_REQUEST,
    changeCompanyPasswordSaga,
  );
  yield takeEvery(
    types.CHANGE_CLIENT_PASSWORD_REQUEST,
    changeClientPasswordSaga,
  );
  yield takeEvery(types.FORGET_PASSWORD_REQUEST, forgetPasswordSaga);
  yield takeEvery(types.GET_PERMISSION_REQUEST, getPermissionSaga);
}
